import { Injectable } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, pipe, throwError, of, from } from 'rxjs';
import { catchError, delay, map, mergeMap, retryWhen, timeout } from 'rxjs/operators';
import { getCurrentUser } from 'aws-amplify/auth';
import { get, post } from 'aws-amplify/api';


@Injectable({
    providedIn: 'root'
})
export class ApiConfigurationsService {

    private userName: string = '';

    constructor() {
        // v6: current user
        getCurrentUser()
            .then((user) => {
                const username = (user as any)?.username as string | undefined;
                this.userName = username ? `${username.split('@')[0]}_` : '';
            })
            .catch(() => {
                this.userName = '';
            });
    }
    public generateRequestResponsePayload(api: IAPI_CONFIGURATIONS, queueId: string): IAPI_INFO {
        return {
            path: api.PATH,
            method: api.METHOD,
            uniqueIdentifier: queueId,
        };
    }
    public reTry<T>(api: IRequest): MonoTypeOperatorFunction<T> {
        return pipe(
            retryWhen((err$) => {
                let retries = 0;
                return err$.pipe(
                    map((error) => {
                        if (retries++ === api.apiConfigurations.RETRY_COUNT) {
                            throw error;
                        }
                        return error;
                    }),
                    delay(api.apiConfigurations.RETRY_DELAY)
                );
            })
        );
    }

    public get<T>(api: IRequest): Observable<T> {
        api.apiConfigurations.METHOD = 'GET';
        const op = get({
            apiName: 'portal',
            path: `${api?.apiConfigurations?.PATH}`,
        });
        return from(op.response.then(async (r) => (await r.body.json()) as T)).pipe(
            timeout(API_CONFIGURATIONS.LAMBDA_TIMEOUT),
            catchError((error) => {
                if ((error?.name || '').toLowerCase() === 'timeouterror') {
                    return this.postAndGetFromQueue<T>(api);
                }
                return throwError(() => error);
            })
        );
    }

    public post<T>(api: IRequest): Observable<T> {
        api.apiConfigurations.METHOD = 'POST';
        const op = post({
            apiName: 'portal',
            path: `${api?.apiConfigurations?.PATH}`,
            options: {
                body: api?.payload,
            },
        });
        return from(op.response.then(async (r) => (await r.body) as T)).pipe(
            timeout(API_CONFIGURATIONS.LAMBDA_TIMEOUT),
            catchError((error) => {
                if ((error?.name || '').toLowerCase() === 'timeouterror') {
                    return this.postAndGetFromQueue<T>(api);
                }
                return throwError(() => error);
            })
        );
    }

    public postAndGetFromQueue<T>(api: IRequest): Observable<T> {
        const requestPath = `request/${api.apiConfigurations.PATH}`;
        const requestOp =
            api.apiConfigurations.METHOD === 'POST'
                ? post({
                    apiName: 'portal',
                    path: `${requestPath}`,
                    options: {
                        body: api.payload
                            ? JSON.parse(JSON.stringify(api.payload))
                            : undefined,
                    },
                })
                : get({
                    apiName: 'portal',
                    path: `${requestPath}`,
                });
        return from(
            requestOp.response.then(async (r) => (await r.body) as any)
        ).pipe(
            catchError(() => {
                const fallback = get({
                    apiName: 'portal',
                    path: `${api.apiConfigurations.PATH}`,
                });
                return from(fallback.response.then(async (r) => (await r.body.json()) as any));
            }),
            delay(api.apiConfigurations.RETRY_DELAY),
            mergeMap((response: any) => {
             
                if (!response?.queueId) {
                    return of(response as T);
                }
          
                const body: IAPI_INFO = this.generateRequestResponsePayload(
                    api.apiConfigurations,
                    response.queueId
                );
                const safeBody = JSON.parse(JSON.stringify(body));
                return from(
                    post({
                        apiName: 'portal',
                        path: 'response/response',
                        options: { body: safeBody },
                    }).response.then(async (r) => (await r.body) as T)
                );
            }),
            this.reTry(api)
        );
    }
    public getConfigurations(api: string): IAPI_CONFIGURATIONS {
        return JSON.parse(JSON.stringify((API_CONFIGURATIONS as any)[api]));
    }
}

export interface IAPI_CONFIGURATIONS {
    RETRY_COUNT: number;
    RETRY_DELAY: number;
    PATH: string;
    METHOD: string;
}
export interface IAPI_INFO {
    path: string;
    method: string;
    uniqueIdentifier: string;
}
export interface IRequest {
    payload?: any;
    apiConfigurations: IAPI_CONFIGURATIONS;
}

export const API_CONFIGURATIONS = {
    LAMBDA_TIMEOUT: 29000,
    GET_PROGRAMS: {
        RETRY_COUNT: 30,
        RETRY_DELAY: 2000,
        PATH: 'programs',
        METHOD: 'GET',
    },
    GET_CLASS_ROASTER_PARTICIPANTS: {
        RETRY_COUNT: 30,
        RETRY_DELAY: 2000,
        PATH: 'programs/${programId}/courses/${courseId}/participants',
        METHOD: 'GET',
    },
};
